import React, { createContext, useContext, useState, useCallback } from 'react';
import { PROJECT_CONFIG } from '../config/projectConfig';
import tagTranslations from '../config/tagTranslations.json';

const LanguageContext = createContext();

const STORAGE_KEY = 'matterpot_language';

export const LanguageProvider = ({ children }) => {
  // Always start in the configured default language (Marathi). Any manual
  // switch made during the session is remembered in localStorage only for
  // use by other components within the same session — a fresh load always
  // resets to Marathi so every visitor begins in the kiosk default.
  const [language, setLanguageState] = useState(() => PROJECT_CONFIG.i18n.defaultLanguage);

  const setLanguage = useCallback((code) => {
    setLanguageState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) {}
  }, []);

  // Translate a tour highlight by sid — reads window._tourTranslations (runtime-fetched)
  // Returns { label, description } for the current language, falling back to 'en'
  const tTour = useCallback((sid) => {
    const translations = window._tourTranslations;
    if (!translations) return { label: null, description: null };
    const entry = translations[sid];
    if (!entry) return { label: null, description: null };
    const langData = entry[language];
    if (langData && langData.label) return { label: langData.label, description: langData.description || null };
    const enData = entry['en'];
    if (enData && enData.label) return { label: enData.label, description: enData.description || null };
    return { label: null, description: null };
  }, [language]);

  // Translate a tag by ID — returns { label, description } for the current language
  const t = useCallback((tagId) => {
    const entry = tagTranslations[tagId];
    if (!entry) return null;

    const langData = entry[language];
    // Check if translation exists and has content
    if (langData && (langData.label || langData.description)) {
      return {
        label: langData.label || null,
        description: langData.description || null,
      };
    }

    // Fallback: return null so the caller can show "Translation unavailable"
    return { label: null, description: null };
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tTour }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
};
